import {
  QUANTITY_NUMBER_FORMAT,
  TOTAL_NUMBER_FORMAT,
  UNITCOST_NUMBER_FORMAT,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { safeWorksheetName } from "../../workbook";
import { generateAlternatesItemsReport, generateEstimateReport } from "../estimate";
import { dateStampSheet } from "../helpers/dynamicSheets";
import { rowNames } from "../helpers/rangeNames";
import { titleMaker } from "./common";

type estimateTypes = "Estimate" | "VM Decisions" | "Rooms" | "Units";

export const createDefaultEstimateTemplate = async (estimateType: estimateTypes = "Estimate") => {
  try {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const reportTitle = estimateType === "Estimate" ? "Detailed Estimate" : estimateType + " Estimate";
      const insertRowName = estimateType === "VM Decisions" ? rowNames.alternateItems : rowNames.lineItems;
      const safeName = await safeWorksheetName(workbook, reportTitle);
      const sheet = workbook.worksheets.add(safeName);
      await context.sync();

      await titleMaker(sheet, reportTitle);
      const rowHeights = [15, 30, 25, 25];
      rowHeights.forEach((height, index) => {
        sheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
      });

      const columnWidths = [20, 400, 70, 50, 100, 120];
      columnWidths.forEach((width, index) => {
        sheet.getRange(`${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`).format.columnWidth =
          width;
      });

      sheet.getRange("1:7").format.verticalAlignment = Excel.VerticalAlignment.center;
      await context.sync();

      const headers: rangeFormatting[] = [
        { rangeAddress: "B6", value: "Name", name: "\\name", Halignment: "Left" },
        { rangeAddress: "C6", value: "Quantity", name: "\\quantity", Halignment: "Center" },
        { rangeAddress: "D6", value: "UOM", name: "\\uom", Halignment: "Center" },
        { rangeAddress: "E6", value: "Unit Cost", name: "\\total_uc", Halignment: "Center" },
        { rangeAddress: "F6", value: "Total", name: "\\total", Halignment: "Right" },
      ];
      headers.forEach((header) => {
        header.fontSize = 12;
        header.bold = true;
      });
      applyRangeFormats(sheet, headers);

      const dataRows: rangeFormatting[] = [
        { rangeAddress: "B7", name: insertRowName },
        { rangeAddress: "C7", numberFormat: QUANTITY_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "D7", Halignment: "Left" },
        { rangeAddress: "E7", numberFormat: UNITCOST_NUMBER_FORMAT, Halignment: "Right" },
        { rangeAddress: "F7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
      ];
      applyRangeFormats(sheet, dataRows);

      await dateStampSheet(sheet);
      applyGridStyle({ range: sheet.getRange("B6:F6"), borderColor: "Black", weight: "Medium", edges: ["EdgeBottom"] });
      await context.sync();
      if (estimateType === "VM Decisions") {
        await generateAlternatesItemsReport(sheet);
      } else {
        await generateEstimateReport(sheet);
      }
      sheet.activate();
      await context.sync();
    });
  } catch (error) {
    throw "Error creating default estimate template: " + error;
  }
};
